import React, { useState } from 'react';
import { Link } from 'react-router-dom';

interface ColorSwatch {
  name: string;
  hex: string;
  className: string;
}

interface ColorGroup {
  title: string;
  description: string;
  colors: ColorSwatch[];
}

const ColorPalette: React.FC = () => {
  const [copiedColor, setCopiedColor] = useState<string | null>(null);

  const colorGroups: ColorGroup[] = [
    {
      title: 'TAL Brown',
      description: 'Cool, earthy browns - grounded and warm tones for natural elements',
      colors: [
        { name: 'Brown 50', hex: '#F5F1ED', className: 'bg-tal-brown-50' },
        { name: 'Brown 100', hex: '#E8DED2', className: 'bg-tal-brown-100' },
        { name: 'Brown 200', hex: '#D4C4B0', className: 'bg-tal-brown-200' },
        { name: 'Brown 300', hex: '#C8B8A8', className: 'bg-tal-brown-300' },
        { name: 'Brown 400', hex: '#A89580', className: 'bg-tal-brown-400' },
        { name: 'Brown 500', hex: '#8B7355', className: 'bg-tal-brown-500' },
        { name: 'Brown 600', hex: '#6F5D45', className: 'bg-tal-brown-600' },
        { name: 'Brown 700', hex: '#5D4E37', className: 'bg-tal-brown-700' },
        { name: 'Brown 800', hex: '#453827', className: 'bg-tal-brown-800' },
        { name: 'Brown 900', hex: '#3D2F1F', className: 'bg-tal-brown-900' },
      ],
    },
    {
      title: 'TAL Blue',
      description: 'Ocean and sky inspired blues - fresh and sophisticated tones',
      colors: [
        { name: 'Blue 50', hex: '#F0F7FA', className: 'bg-tal-blue-50' },
        { name: 'Blue 100', hex: '#E6F0F5', className: 'bg-tal-blue-100' },
        { name: 'Blue 200', hex: '#C2DBE8', className: 'bg-tal-blue-200' },
        { name: 'Blue 300', hex: '#A8C5DA', className: 'bg-tal-blue-300' },
        { name: 'Blue 400', hex: '#7AA5C0', className: 'bg-tal-blue-400' },
        { name: 'Blue 500', hex: '#5B8AA8', className: 'bg-tal-blue-500' },
        { name: 'Blue 600', hex: '#4A7189', className: 'bg-tal-blue-600' },
        { name: 'Blue 700', hex: '#2D5366', className: 'bg-tal-blue-700' },
        { name: 'Blue 800', hex: '#233F4D', className: 'bg-tal-blue-800' },
        { name: 'Blue 900', hex: '#1A3544', className: 'bg-tal-blue-900' },
      ],
    },
    {
      title: 'TAL Slate',
      description: 'Bridging accent colors - neutral tones that complement both brown and blue',
      colors: [
        { name: 'Slate 50', hex: '#F1F3F5', className: 'bg-tal-slate-50' },
        { name: 'Slate 100', hex: '#E3E7EB', className: 'bg-tal-slate-100' },
        { name: 'Slate 200', hex: '#C5CDD5', className: 'bg-tal-slate-200' },
        { name: 'Slate 300', hex: '#A0AAB5', className: 'bg-tal-slate-300' },
        { name: 'Slate 400', hex: '#7A8794', className: 'bg-tal-slate-400' },
        { name: 'Slate 500', hex: '#5D6B78', className: 'bg-tal-slate-500' },
        { name: 'Slate 600', hex: '#4A5662', className: 'bg-tal-slate-600' },
        { name: 'Slate 700', hex: '#3A444E', className: 'bg-tal-slate-700' },
        { name: 'Slate 800', hex: '#2B3339', className: 'bg-tal-slate-800' },
        { name: 'Slate 900', hex: '#1D2327', className: 'bg-tal-slate-900' },
      ],
    },
  ];

  const handleCopyColor = (hex: string, className: string) => {
    navigator.clipboard.writeText(className);
    setCopiedColor(className);
    setTimeout(() => setCopiedColor(null), 2000);
  };

  return (
    <div className="min-h-screen bg-tal-slate-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <Link to="/" className="text-tal-blue-600 hover:text-tal-blue-800 flex items-center gap-2 font-medium">
            ← Back to Home
          </Link>
        </div>

        <div className="text-center mb-12">
          <h1 className="text-5xl font-bold text-tal-slate-900 mb-4">
            Tales and Legends Color Palette
          </h1>
          <p className="text-xl text-tal-slate-700 max-w-3xl mx-auto">
            A carefully crafted palette of cool browns and blues, designed to evoke the warmth of legends and the depth of epic tales.
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-8 mb-12">
          <h2 className="text-2xl font-bold text-tal-slate-900 mb-4">How to Use</h2>
          <p className="text-tal-slate-700 mb-4">
            Click any color swatch to copy its Tailwind class name to your clipboard. Use these colors throughout your Tales and Legends project by referencing the class names in your components.
          </p>
          <div className="bg-tal-slate-100 rounded p-4 font-mono text-sm">
            <code className="text-tal-slate-800">
              &lt;div className="<span className="text-tal-blue-600">bg-tal-brown-500</span> <span className="text-tal-blue-600">text-tal-blue-100</span>"&gt;
            </code>
          </div>
        </div>

        <div className="space-y-12">
          {colorGroups.map((group) => (
            <div key={group.title} className="bg-white rounded-lg shadow-lg p-8">
              <h2 className="text-3xl font-bold text-tal-slate-900 mb-2">{group.title}</h2>
              <p className="text-tal-slate-600 mb-6">{group.description}</p>

              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 lg:grid-cols-10 gap-4">
                {group.colors.map((color) => (
                  <div
                    key={color.className}
                    className="cursor-pointer transform transition-transform hover:scale-105"
                    onClick={() => handleCopyColor(color.hex, color.className)}
                  >
                    <div className={`${color.className} h-20 rounded-lg shadow-md mb-2 border border-tal-slate-200`} />
                    <p className="text-xs font-semibold text-tal-slate-800 mb-1">{color.name}</p>
                    <p className="text-xs text-tal-slate-500 font-mono">{color.hex}</p>
                    {copiedColor === color.className && (
                      <p className="text-xs text-tal-blue-600 font-semibold mt-1">Copied!</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 bg-gradient-to-r from-tal-brown-500 via-tal-slate-600 to-tal-blue-600 rounded-lg shadow-lg p-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Color Combinations</h2>
          <p className="text-white mb-8 max-w-2xl mx-auto opacity-90">
            These colors work beautifully together. Mix browns for warmth, blues for freshness, and slate for balance.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <div className="bg-tal-brown-600 text-tal-blue-100 p-6 rounded-lg">
              <h3 className="font-bold mb-2">Warm & Cool</h3>
              <p className="text-sm opacity-90">Brown 600 + Blue 100</p>
            </div>
            <div className="bg-tal-blue-700 text-tal-brown-50 p-6 rounded-lg">
              <h3 className="font-bold mb-2">Deep & Light</h3>
              <p className="text-sm opacity-90">Blue 700 + Brown 50</p>
            </div>
            <div className="bg-tal-slate-800 text-tal-blue-200 p-6 rounded-lg">
              <h3 className="font-bold mb-2">Neutral & Accent</h3>
              <p className="text-sm opacity-90">Slate 800 + Blue 200</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ColorPalette;
